import { ApolloProvider } from '@apollo/react-hooks'
import { ApolloClient } from 'apollo-client'
import { ApolloLink } from 'apollo-link'
import React from 'react'
import { CacheShape } from '../../generated/graphql'
import { CACHE_ALWAYS_ON_LOAD } from './helpers/cacheDefaults'
import { ApolloClientLinksEnum } from './helpers/clientsEnum'
import { sparkLink as link, unifiedLink as unifiedApolloLink } from '@edge/spark/io-apollo'
import updateUser from './helpers/updateUser'
import resolvers from './resolvers'
import { cache, persistor } from './apolloSingletons'

const INVALID_USER_TITLE = 'Your account is not configured correctly!'
const INVALID_USER_CONTENT = 'Please contact a system administrator.'

interface Props {
    children: React.ReactNode
}

const combinedLinks = ApolloLink.split(
    (operation) => operation.getContext().clientName === ApolloClientLinksEnum.unifiedApi,
    unifiedApolloLink, // <= apollo will send to this if the above condition is true
    link, // <= otherwise will send to this
)

// unifiedClient exported for use outside this component tree
export { unifiedClient } from './apolloSingletons'

const ApolloConfig: React.FC<Props> = ({ children }: Props) => {
    const [client, setClient] = React.useState<ApolloClient<CacheShape>>()

    React.useEffect(() => {
        ;(async () => {
            const ac = new ApolloClient<CacheShape>({
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore graphql peer version mismatch (graphql@16 vs apollo-cache-inmemory@1.x)
                cache,
                link: combinedLinks,
                resolvers,
                defaultOptions: {
                    watchQuery: {
                        fetchPolicy: 'cache-and-network',
                        errorPolicy: 'all',
                        returnPartialData: true,
                    },
                    query: {
                        fetchPolicy: 'network-only',
                        errorPolicy: 'all',
                    },
                    mutate: {
                        errorPolicy: 'all',
                    },
                },
            })

            try {
                await persistor.restore()
            } catch (error) {
                console.error('Apollo cache restore failed', error)
            }

            // Write only the always-reset fields — persisted values (e.g. layouts)
            // were already restored by persistor.restore() above and must not be overwritten.
            ac.writeData({
                data: CACHE_ALWAYS_ON_LOAD,
            })

            ac.onResetStore(() =>
                Promise.resolve(
                    cache.writeData({
                        data: CACHE_ALWAYS_ON_LOAD,
                    }),
                ),
            )

            setClient(ac)

            await updateUser(ac).catch(() => {
                persistor
                    .purge()
                    .then(() => updateUser(ac))
                    .catch(() => {
                        ac.writeData({
                            data: {
                                profile: null,
                                organizations: [],
                                activeOrganization: null,
                                dialog: {
                                    id: '1',
                                    heading: INVALID_USER_TITLE,
                                    message: INVALID_USER_CONTENT,
                                    type: 'ALERT',
                                    __typename: 'Dialog',
                                },
                            },
                        })
                    })
            })
        })()
    }, [])

    return client ? <ApolloProvider client={client}>{children}</ApolloProvider> : null
}

export default ApolloConfig
